import React, { useState, useEffect } from "react";
import Joi from "joi-browser";
import AddSechduleTime from "./AddSechduleTime";

const ScheduleMeetingPopup = ({
  partitem,
  handleSubmit,
  selectedItem,
  handleEditAppointment,
  onClose,
  isLoading,
}) => {
  const [scheduleList, setScheduleList] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [meetingProviders, setMeetingProviders] = useState([]);
  const [secShowHide, setSecShowHide] = useState(false);
  const [meetingJson, setMeetingJson] = useState("");
  const [errors, setErrors] = useState({});

  const schema = {
    remarks: Joi.string().required().label("Comment"),
    schedule: Joi.string().required().label("Schedule Meeting"),
  };

  // Sync state when selectedItem changes (edit mode vs. create mode)
  useEffect(() => {
    if (selectedItem) {
      setRemarks(selectedItem.remarks || "");
      if (selectedItem.meetingOptions) {
        const schdList = selectedItem.meetingOptions.map((o) => {
          const obj = { ...o };
          const date = obj.meetingDate.split("T")[0];
          obj.meetingDate = date;
          return obj;
        });
        setScheduleList(schdList);
      } else {
        setScheduleList([]);
      }
      let providers = selectedItem?.meetingProvider?.split(",");
      if (!providers) providers = [];
      setMeetingProviders(providers.map((p) => p.trim()).filter(Boolean));
    } else {
      setRemarks("");
      setScheduleList([]);
      setMeetingProviders([]);
      setMeetingJson("");
      setErrors({});
    }
  }, [selectedItem]);

  const handleSecShowHide = () => {
    setSecShowHide((prev) => !prev);
  };

  const handleScheduleDelete = (id) => {
    const updated = scheduleList.filter((x) => x.optionId !== id);
    setScheduleList(updated);
    validateScheduleMeeting(updated);
  };

  const validateScheduleMeeting = (currentList) => {
    const newErrors = { ...errors };
    if (currentList.length > 0) {
      delete newErrors.schedule;
    } else {
      const obj = { schedule: "" };
      const propSchema = { schedule: schema.schedule };
      const { error } = Joi.validate(obj, propSchema);
      newErrors.schedule = error.details[0].message;
    }
    setErrors(newErrors);
  };

  const validateRemarks = (value) => {
    const newErrors = { ...errors };
    const obj = { remarks: value };
    const propSchema = { remarks: schema.remarks };
    const { error } = Joi.validate(obj, propSchema);
    if (error) {
      newErrors.remarks = error.details[0].message;
    } else {
      delete newErrors.remarks;
    }
    setErrors(newErrors);
    return error ? newErrors : null;
  };

  const handleRemarksChange = (e) => {
    const val = e.target.value;
    setRemarks(val);
    validateRemarks(val);
  };

  const validateAll = () => {
    const dataObj = { remarks, schedule: scheduleList.length > 0 ? "hasSlots" : "" };
    const checkSchema = { remarks: schema.remarks, schedule: schema.schedule };
    const options = { abortEarly: false, allowUnknown: true };
    const { error } = Joi.validate(dataObj, checkSchema, options);
    if (!error) return null;

    const newErrors = {};
    for (let item of error.details) {
      if (item.type === "any.required" || item.type === "any.empty") {
        newErrors[item.path[0]] = item.message;
      }
    }
    return Object.keys(newErrors).length > 0 ? newErrors : null;
  };

  const handleMeetingProvider = ({ currentTarget }) => {
    let newMeetings = [...meetingProviders];
    if (currentTarget.checked) {
      newMeetings.push(currentTarget.value);
    } else {
      newMeetings = newMeetings.filter((x) => x !== currentTarget.value);
    }
    setMeetingProviders(newMeetings);
  };

  const handleSubmitSchedule = (events) => {
    const newScheduleList = [];
    events.map((item) => {
      const dt = new Date(item.start);
      if (item.slots.length > 0) {
        const strTime = new Date(item.slots[0])
          .toLocaleTimeString()
          .replace(/(.*)\D\d+/, "$1");
        const endTime = new Date(item.slots[item.slots.length - 1])
          .toLocaleTimeString()
          .replace(/(.*)\D\d+/, "$1");
        newScheduleList.push({
          optionId: item.optionId,
          meetingDate: dt.toDateString(),
          startTime: strTime,
          endTime: endTime,
          notes: item.title,
          slotsJson: JSON.stringify(item.slots),
        });
      }
    });
    setSecShowHide(false);
    setMeetingJson(JSON.stringify(events));
    setScheduleList(newScheduleList);

    // Clear schedule validation error since slots are now present
    const newErrors = { ...errors };
    delete newErrors.schedule;
    setErrors(newErrors);
  };

  const doSubmit = () => {
    const validationErrors = validateAll();
    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }

    let providers = meetingProviders.join(",");

    if (selectedItem?.meetingId !== null && selectedItem?.meetingId !== undefined) {
      const updatedItem = {
        ...selectedItem,
        remarks: remarks,
        meetingOptions: scheduleList,
        meetingProvider: providers,
      };
      handleEditAppointment(updatedItem);
    } else {
      const data = { remarks: remarks };
      handleSubmit(data, scheduleList, meetingJson, providers);
    }
  };

  const getCalendarDateParts = (dateStr) => {
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) {
        return { month: "DATE", day: "Option", weekday: "Option" };
      }
      const month = d.toLocaleString("default", { month: "short" }).toUpperCase();
      const day = d.getDate();
      const weekday = d.toLocaleString("default", { weekday: "short" });
      return { month, day, weekday };
    } catch {
      return { month: "DATE", day: "Option", weekday: "Option" };
    }
  };

  const hasProvider = (val) => meetingProviders.includes(val);
  const isEditMode = selectedItem?.meetingId !== null && selectedItem?.meetingId !== undefined;
  const isInvalid = validateAll() !== null;

  return (
    <div className="modern-popup-container">
      <style>{`
        :root {
          --primary-color: #2563eb;
          --primary-hover: #1d4ed8;
          --primary-soft: #eff6ff;
          --primary-glow: rgba(37, 99, 235, 0.12);
          --secondary-color: #64748b;
          --secondary-hover: #475569;
          --border-color: #e2e8f0;
          --bg-light: #f8fafc;
          --text-main: #0f172a;
          --text-muted: #64748b;
          --radius-lg: 0px;
          --radius-md: 0px;
          --radius-sm: 0px;
          --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          --shadow-md: 0 4px 12px -2px rgb(0 0 0 / 0.05), 0 2px 6px -1px rgb(0 0 0 / 0.03);
          --shadow-lg: 0 10px 25px -5px rgb(0 0 0 / 0.05), 0 8px 16px -6px rgb(0 0 0 / 0.03);
        }

        .modern-popup-container {
          background: #ffffff;
          padding: 20px 24px;
          border: 1px solid #cbd5e1;
          border-radius: 0 !important;
          color: #1e293b;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          box-shadow: none !important;
        }

        .section-header-wrap {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e2e8f0;
        }

        .section-label {
          font-size: 12px !important;
          font-weight: 700 !important;
          color: #334155 !important;
          margin-bottom: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 8px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.04em !important;
          background: transparent !important;
          padding: 0 !important;
          border-radius: 0 !important;
        }

        .form-field-group {
          margin-bottom: 20px;
        }

        .form-label {
          font-size: 12px !important;
          font-weight: 700 !important;
          color: #334155 !important;
          margin-bottom: 6px !important;
          display: block !important;
          text-transform: uppercase !important;
          letter-spacing: 0.04em !important;
        }

        .modern-textarea {
          width: 100%;
          border: 1px solid #cbd5e1 !important;
          border-radius: 0 !important;
          padding: 8px 12px !important;
          font-size: 13.5px !important;
          line-height: 1.5;
          transition: all 0.2s ease !important;
          background-color: #fff;
          color: #1e293b !important;
          resize: vertical;
          min-height: 100px;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
        }

        .modern-textarea:focus {
          outline: none;
          border-color: #2563eb !important;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12) !important;
        }

        .wf-popup-footer {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
        }

        .wf-btn {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
          padding: 7px 18px !important;
          font-size: 13.5px !important;
          font-weight: 500 !important;
          border-radius: 0 !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          border: 1px solid transparent !important;
          min-width: 120px !important;
          outline: none !important;
          box-shadow: 0 2px 5px rgba(15, 23, 42, 0.06) !important;
        }

        .wf-btn:disabled {
          opacity: 0.85 !important;
          cursor: not-allowed !important;
          background: #cbd5e1 !important;
          border-color: #cbd5e1 !important;
          color: #94a3b8 !important;
          box-shadow: none !important;
          transform: none !important;
        }

        .wf-btn-primary {
          background: #2563eb !important;
          color: #ffffff !important;
          border: 1px solid #2563eb !important;
          box-shadow: 0 2px 5px rgba(37, 99, 235, 0.2) !important;
        }

        .wf-btn-primary:hover:not(:disabled) {
          background: #1d4ed8 !important;
          border-color: #1d4ed8 !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3) !important;
        }

        .wf-btn-secondary {
          background: #ffffff !important;
          color: #1e293b !important;
          border: 1px solid #cbd5e1 !important;
        }

        .wf-btn-secondary:hover:not(:disabled) {
          background: #f8fafc !important;
          color: #0f172a !important;
          border-color: #94a3b8 !important;
          transform: translateY(-1px) !important;
        }

        .wf-btn-propose {
          border: 1px solid #2563eb !important;
          color: #ffffff !important;
          background: #2563eb !important;
          border-radius: 0 !important;
          font-weight: 500 !important;
          font-size: 13px !important;
          padding: 6px 14px !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 6px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          box-shadow: 0 2px 5px rgba(37, 99, 235, 0.2) !important;
        }

        .wf-btn-propose:hover {
          background: #1d4ed8 !important;
          border-color: #1d4ed8 !important;
          transform: translateY(-1px) !important;
        }

        .rt-popup-empty-icon-wrap {
          width: 48px !important;
          height: 48px !important;
          border-radius: 0 !important;
          background: #f8fafc !important;
          border: 1px solid #cbd5e1 !important;
          color: #2563eb !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-size: 20px !important;
          margin-bottom: 4px !important;
        }

        .rt-popup-empty-alert {
          font-size: 13.5px !important;
          color: #64748b !important;
          text-align: center !important;
          padding: 28px 20px !important;
          border: 1px dashed #cbd5e1 !important;
          border-radius: 0 !important;
          background: #f8fafc !important;
          cursor: pointer !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
          transition: all 0.2s ease !important;
        }

        .rt-popup-empty-alert:hover {
          border-color: #94a3b8 !important;
          background: #f1f5f9 !important;
        }

        .rt-popup-slots-list {
          display: grid !important;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)) !important;
          gap: 12px !important;
          margin-top: 10px !important;
        }

        .rt-popup-slot-card {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          gap: 16px !important;
          padding: 8px 12px !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 0 !important;
          background: #ffffff !important;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
          transition: all 0.2s ease !important;
        }

        .rt-popup-slot-card:hover {
          border-color: #94a3b8 !important;
          transform: translateY(-1px) !important;
        }

        .rt-popup-slot-info {
          display: flex !important;
          align-items: center !important;
          gap: 12px !important;
        }

        .slot-calendar-badge {
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
          width: 44px !important;
          height: 46px !important;
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 0 !important;
          overflow: hidden !important;
          flex-shrink: 0 !important;
        }

        .cal-month {
          display: block !important;
          width: 100% !important;
          background: #f1f5f9 !important;
          color: #334155 !important;
          font-size: 10px !important;
          font-weight: 700 !important;
          text-align: center !important;
          padding: 2px 0 !important;
          letter-spacing: 0.05em !important;
          border-bottom: 1px solid #cbd5e1 !important;
        }

        .cal-day {
          display: block !important;
          font-size: 15px !important;
          font-weight: 700 !important;
          color: #1e293b !important;
          text-align: center !important;
          line-height: 1.3 !important;
        }

        .slot-time-details {
          display: flex !important;
          flex-direction: column !important;
          gap: 2px !important;
        }

        .slot-weekday {
          font-size: 11px !important;
          font-weight: 600 !important;
          color: #64748b !important;
          text-transform: uppercase !important;
          letter-spacing: 0.04em !important;
        }

        .slot-time-range {
          font-size: 13px !important;
          font-weight: 500 !important;
          color: #1e293b !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 6px !important;
          background: #f8fafc !important;
          padding: 3px 8px !important;
          border-radius: 0 !important;
          border: 1px solid #e2e8f0 !important;
        }

        .rt-popup-slot-delete-btn {
          background: transparent !important;
          border: 1px solid transparent !important;
          color: #64748b !important;
          cursor: pointer !important;
          padding: 6px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          transition: all 0.2s ease !important;
          border-radius: 0 !important;
          width: 28px !important;
          height: 28px !important;
        }

        .rt-popup-slot-delete-btn:hover {
          background: #fee2e2 !important;
          color: #dc2626 !important;
          border-color: #fca5a5 !important;
        }

        .rt-provider-section {
          margin-top: 20px !important;
          margin-bottom: 20px !important;
          width: 100% !important;
        }

        .rt-provider-grid {
          display: grid !important;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)) !important;
          gap: 12px !important;
          margin-top: 10px !important;
        }

        .rt-provider-card {
          border: 1px solid #cbd5e1 !important;
          border-radius: 0 !important;
          padding: 12px 14px !important;
          background: #ffffff !important;
          transition: all 0.2s ease !important;
          cursor: pointer !important;
          display: flex !important;
          align-items: center !important;
          position: relative !important;
          overflow: hidden !important;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
          user-select: none !important;
        }

        .rt-provider-card:hover {
          border-color: #94a3b8 !important;
          background: #f8fafc !important;
          transform: translateY(-1px) !important;
        }

        .rt-provider-card.is-checked {
          border-color: #2563eb !important;
          background: #eff6ff !important;
          box-shadow: 0 2px 6px rgba(37, 99, 235, 0.12) !important;
        }

        .rt-provider-icon-wrapper {
          width: 36px !important;
          height: 36px !important;
          border-radius: 0 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-size: 16px !important;
          margin-right: 12px !important;
          flex-shrink: 0 !important;
          border: 1px solid #cbd5e1 !important;
          background: #ffffff !important;
        }

        .icon-teams { color: #4f46e5 !important; }
        .icon-zoom { color: #0284c7 !important; }
        .icon-meet { color: #16a34a !important; }

        .rt-provider-check-indicator {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 18px;
          height: 18px;
          border-radius: 0;
          background: #2563eb;
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          opacity: 0;
          transition: all 0.2s ease;
        }

        .rt-provider-card.is-checked .rt-provider-check-indicator {
          opacity: 1;
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #ef4444;
          font-size: 12.5px;
          font-weight: 500;
          margin-top: 6px;
        }
      `}</style>

      <div className="row">
        <div className="col-12">
          <div className="section-header-wrap">
            <label className="section-label">Proposed Date & Time Options</label>
            <button
              type="button"
              className="wf-btn-propose"
              onClick={handleSecShowHide}
            >
              <i className="fa fa-plus" /> Propose Slot
            </button>
          </div>

          {scheduleList.length === 0 ? (
            <div className="rt-popup-empty-alert" onClick={handleSecShowHide}>
              <div className="rt-popup-empty-icon-wrap animate-bounce-subtle">
                <i className="fa fa-calendar-plus-o" aria-hidden="true" />
              </div>
              <p style={{ margin: 0, fontWeight: 800, fontSize: "15px", color: "var(--text-main)" }}>
                No slots proposed yet
              </p>
              <span style={{ fontSize: "12.5px", color: "var(--text-muted)", maxWidth: "280px" }}>
                Propose one or more dates and times. The applicant can choose their preferred slot from these options.
              </span>
              <button
                type="button"
                className="wf-btn wf-btn-primary"
                style={{ marginTop: "12px", minWidth: "150px", padding: "8px 16px", fontSize: "13px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSecShowHide();
                }}
              >
                <i className="fa fa-plus" /> Propose First Slot
              </button>
            </div>
          ) : (
            <div className="rt-popup-slots-list">
              {scheduleList.map((item) => {
                const { month, day, weekday } = getCalendarDateParts(item.meetingDate);
                return (
                  <div key={item.optionId} className="rt-popup-slot-card">
                    <div className="rt-popup-slot-info">
                      <div className="slot-calendar-badge">
                        <span className="cal-month">{month}</span>
                        <span className="cal-day">{day}</span>
                      </div>
                      <div className="slot-time-details">
                        <span className="slot-weekday">{weekday}</span>
                        <span className="slot-time-range">
                          <i className="fa fa-clock-o" /> {item.startTime} - {item.endTime}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="rt-popup-slot-delete-btn"
                      onClick={() => handleScheduleDelete(item.optionId)}
                      title="Delete slot"
                    >
                      <i className="fa fa-trash-o" aria-hidden="true" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {errors.schedule && (
            <div className="error-message">
              <i className="fa fa-exclamation-circle" aria-hidden="true" />
              {errors.schedule}
            </div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="rt-provider-section">
            <label className="section-label">Choose Meeting Platform(s)</label>
            <div className="rt-provider-grid">
              <div
                className={`rt-provider-card ${hasProvider("team") ? "is-checked" : ""}`}
                onClick={() => {
                  const mockEvent = {
                    currentTarget: {
                      checked: !hasProvider("team"),
                      value: "team",
                    },
                  };
                  handleMeetingProvider(mockEvent);
                }}
              >
                <div className="rt-provider-check-indicator">
                  <i className="fa fa-check" aria-hidden="true" />
                </div>
                <div className="rt-provider-icon-wrapper icon-teams">
                  <i className="fa fa-windows" aria-hidden="true" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 700, fontSize: "14px", color: "var(--text-main)", display: "block" }}>
                    Microsoft Teams
                  </span>
                  <span
                    className="text-muted"
                    style={{ fontSize: "11px", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    title={partitem?.contact1?.performeremail}
                  >
                    {partitem?.contact1?.performeremail || "User Email"}
                  </span>
                </div>
              </div>

              <div
                className={`rt-provider-card ${hasProvider("zoom") ? "is-checked" : ""}`}
                onClick={() => {
                  const mockEvent = {
                    currentTarget: {
                      checked: !hasProvider("zoom"),
                      value: "zoom",
                    },
                  };
                  handleMeetingProvider(mockEvent);
                }}
              >
                <div className="rt-provider-check-indicator">
                  <i className="fa fa-check" aria-hidden="true" />
                </div>
                <div className="rt-provider-icon-wrapper icon-zoom">
                  <i className="fa fa-video-camera" aria-hidden="true" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 700, fontSize: "14px", color: "var(--text-main)", display: "block" }}>
                    Zoom Meeting
                  </span>
                  <span className="text-muted" style={{ fontSize: "11px", display: "block" }}>
                    Propose Zoom link
                  </span>
                </div>
              </div>

              <div
                className={`rt-provider-card ${hasProvider("meet") ? "is-checked" : ""}`}
                onClick={() => {
                  const mockEvent = {
                    currentTarget: {
                      checked: !hasProvider("meet"),
                      value: "meet",
                    },
                  };
                  handleMeetingProvider(mockEvent);
                }}
              >
                <div className="rt-provider-check-indicator">
                  <i className="fa fa-check" aria-hidden="true" />
                </div>
                <div className="rt-provider-icon-wrapper icon-meet">
                  <i className="fa fa-google" aria-hidden="true" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 700, fontSize: "14px", color: "var(--text-main)", display: "block" }}>
                    Google Meet
                  </span>
                  <span className="text-muted" style={{ fontSize: "11px", display: "block" }}>
                    Propose Meet link
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 form-field-group">
          <label htmlFor="remarks" className="form-label">Notes / Remarks</label>
          <textarea
            name="remarks"
            id="remarks"
            className="modern-textarea"
            placeholder="Enter details or notes regarding the schedule..."
            value={remarks}
            onChange={handleRemarksChange}
          />
          {errors.remarks && (
            <div className="error-message">
              <i className="fa fa-exclamation-circle" aria-hidden="true" />
              {errors.remarks}
            </div>
          )}
        </div>
      </div>

      <div className="wf-popup-footer">
        {onClose && (
          <button
            className="wf-btn wf-btn-secondary"
            onClick={onClose}
            type="button"
            disabled={isLoading}
          >
            <i className="fa fa-arrow-left" aria-hidden="true" />
            Back
          </button>
        )}
        <button
          className="wf-btn wf-btn-primary"
          onClick={doSubmit}
          type="button"
          disabled={isLoading || isInvalid}
        >
          {isLoading ? (
            <>
              <i className="fa fa-spinner fa-spin" aria-hidden="true" />
              {isEditMode ? "Saving..." : "Sending..."}
            </>
          ) : (
            <>
              <i
                className={`fa ${isEditMode ? "fa-save" : "fa-paper-plane"}`}
                aria-hidden="true"
              />
              {isEditMode ? "Save Changes" : "Send Request"}
            </>
          )}
        </button>
      </div>

      <AddSechduleTime
        secShowHide={secShowHide}
        handleSecShowHide={handleSecShowHide}
        handleSubmitSchedule={handleSubmitSchedule}
      />
    </div>
  );
};

export default ScheduleMeetingPopup;
